'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from '@shared/all/domains/posts';
import { isCardPostId } from '@shared/all/helpers/board-agent-core/boardTerminal';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import type { JobTarget } from '../../hooks/useJobTerminal';
import { RunnerProvider } from '../../hooks/useRunnerContext';
import { client } from '../../utils/trpc';

// Both modals pull in xterm and the diff viewer, which no page outside a board
// needs until a job is actually opened.
const BareCardModal = dynamic(() => import('../pages/space/BareCardModal'), {
  ssr: false
});
const CardAIModal = dynamic(() => import('../pages/space/CardAIModal'), {
  ssr: false
});
type Props = {
  job: JobTarget;
  onClose: () => void;
};

/**
 * Opens one job's session modal outside its board. The board page normally
 * supplies the RunnerProvider and the card, so both are rebuilt here: the
 * provider is pinned to the runner the job runs on, and the card is fetched
 * because the panel only carries its title.
 */
const JobTerminalOverlay = t0 => {
  const $ = _c(24);
  const {
    job,
    onClose
  } = t0;
  let t1;
  if ($[0] !== job.postId) {
    t1 = isCardPostId(job.postId);
    $[0] = job.postId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isCard = t1;
  let t2;
  let t3;
  if ($[2] !== job.postId) {
    t2 = ["terminalCardPost", job.postId];
    t3 = () => unwrapFromResult(client.posts.singlePost.query({
      postId: job.postId
    }));
    $[2] = job.postId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isCard || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      enabled: isCard,
      staleTime: 60000
    };
    $[5] = isCard;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data
  } = useQuery(t4);
  const card = data?.posts?.[0];
  const t5 = card?.title || job.title;
  const t6 = card?.content || "";
  const t7 = card?.contentRows ?? 1;
  let t8;
  if ($[9] !== job.postId || $[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t8 = {
      id: job.postId,
      title: t5,
      content: t6,
      contentRows: t7
    };
    $[9] = job.postId;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const post = t8;
  let t9;
  if ($[14] !== job.mode || $[15] !== job.runnerId || $[16] !== job.spaceSlug || $[17] !== onClose || $[18] !== post) {
    t9 = job.mode === "bare" ? <BareCardModal post={post} spaceSlug={job.spaceSlug} pinnedRunnerId={job.runnerId} closeHandler={onClose} /> : <CardAIModal post={post} mode="claude" closeHandler={onClose} />;
    $[14] = job.mode;
    $[15] = job.runnerId;
    $[16] = job.spaceSlug;
    $[17] = onClose;
    $[18] = post;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== job.runnerId || $[21] !== job.spaceSlug || $[22] !== t9) {
    t10 = <RunnerProvider spaceSlug={job.spaceSlug} pinnedRunnerId={job.runnerId}>{t9}</RunnerProvider>;
    $[20] = job.runnerId;
    $[21] = job.spaceSlug;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
export default JobTerminalOverlay;